import { API_BASE } from "../../config.js";
import { state } from "../../state/store.js";

const avatarCache = new Map();
const avatarInflight = new Map();

const AVATAR_NEG_TTL_MS = 24 * 60 * 60 * 1000;
const AVATAR_NEG_PREFIX = "wcrm.avatar-empty.v1:";
const AVATAR_MAX_CONCURRENT = 4;

let avatarActive = 0;
const avatarQueue = [];

export function getAvatarUrl(jid, { force = false, cacheOnly = false, syncAvatar = false } = {}) {
  if (force) {
    avatarCache.delete(jid);
    avatarInflight.delete(jid);
    clearAvatarNegCache(jid);
  }
  const cached = avatarCache.get(jid);
  if (cached && cached.expiresAt > Date.now()) {
    const shouldBypassCacheOnlyMiss = !cached.url && cached.cacheOnly && syncAvatar && !cacheOnly;
    if (!shouldBypassCacheOnlyMiss) return Promise.resolve(cached.url);
  }
  if (avatarInflight.has(jid)) return avatarInflight.get(jid);

  if (readAvatarNegCache(jid)) {
    avatarCache.set(jid, { url: null, expiresAt: Date.now() + 60 * 60 * 1000 });
    return Promise.resolve(null);
  }

  const params = new URLSearchParams();
  if (cacheOnly) params.set("cacheOnly", "1");
  if (force || syncAvatar) params.set("syncAvatar", "1");
  const qs = params.toString() ? `?${params.toString()}` : "";
  const promise = scheduleAvatar(() => fetch(
    `${API_BASE}/whatsapp/contacts/${encodeURIComponent(jid)}/picture${qs}`,
    { headers: { Authorization: `Bearer ${state.authToken}` }, cache: "no-store" },
  ).then(async (resp) => {
    if (resp.status === 204) {
      if (!cacheOnly) writeAvatarNegCache(jid);
      return null;
    }
    if (!resp.ok) return null;
    const publicUrl = resp.headers.get("X-Avatar-Url");
    if (publicUrl) return publicUrl;
    const blob = await resp.blob();
    return URL.createObjectURL(blob);
  })).then((url) => {
    avatarCache.set(jid, { url, expiresAt: Date.now() + 60 * 60 * 1000, cacheOnly });
    avatarInflight.delete(jid);
    return url;
  }).catch(() => {
    avatarCache.set(jid, { url: null, expiresAt: Date.now() + 5 * 60 * 1000 });
    avatarInflight.delete(jid);
    return null;
  });
  avatarInflight.set(jid, promise);
  return promise;
}

function scheduleAvatar(task) {
  return new Promise((resolve, reject) => {
    const run = () => {
      avatarActive++;
      task()
        .then((value) => {
          avatarActive--;
          resolve(value);
          pumpAvatarQueue();
        })
        .catch((err) => {
          avatarActive--;
          reject(err);
          pumpAvatarQueue();
        });
    };
    if (avatarActive < AVATAR_MAX_CONCURRENT) run();
    else avatarQueue.push(run);
  });
}

function pumpAvatarQueue() {
  while (avatarActive < AVATAR_MAX_CONCURRENT && avatarQueue.length) {
    const next = avatarQueue.shift();
    next();
  }
}

function readAvatarNegCache(jid) {
  try {
    const raw = localStorage.getItem(AVATAR_NEG_PREFIX + jid);
    if (!raw) return false;
    const ts = Number(raw);
    if (Date.now() - ts > AVATAR_NEG_TTL_MS) {
      localStorage.removeItem(AVATAR_NEG_PREFIX + jid);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

function writeAvatarNegCache(jid) {
  try {
    localStorage.setItem(AVATAR_NEG_PREFIX + jid, String(Date.now()));
  } catch {
    /* ignore quota errors */
  }
}

function clearAvatarNegCache(jid) {
  try {
    localStorage.removeItem(AVATAR_NEG_PREFIX + jid);
  } catch {
    /* ignore storage errors */
  }
}
